import Header from "../components/Header";
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import {
    Alert, Avatar, Box, Button, Card, Chip, Link, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { MascaraCpf } from "../../utils/MascaraCpf";
import { MascaraTelefone } from "../../utils/MascaraTelefone";
import { CabecalhoUsuarios, CampoBusca, campo, corPrimaria, filtrarUsuarios, iniciais } from "./components/Comum";

const tiposPessoa = { PF: "Pessoa física", PJ: "Pessoa jurídica", pessoaFisica: "Pessoa física", pessoaJuridica: "Pessoa jurídica" };

const filtros = [
    { chave: "todos", label: "Todos" },
    { chave: "titulares", label: "Titulares" },
    { chave: "secretarias", label: "Secretárias" },
];

// Secretárias trabalham na conta do titular (responsavel); o perfil delas é o do titular
const ehSecretaria = (usuario) => !!usuario?.secretaria;
const idPerfil = (usuario) => (ehSecretaria(usuario) ? usuario?.responsavel?.idUsuario : usuario?.idUsuario);

function Usuarios() {

    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState("");
    const [busca, setBusca] = useState("");
    const [filtro, setFiltro] = useState("todos");
    const [pagina, setPagina] = useState(0);
    const [porPagina, setPorPagina] = useState(10);

    useEffect(() => {
        const getUsuarios = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarUsuarios`, {
                    headers: {
                        Authorization: token
                    }
                })
                // Sem clientes o back responde 400 e não manda a lista
                const lista = response.data.usuarios || [];
                setUsuarios([...lista].sort((a, b) => String(campo(a, "nome")).localeCompare(String(campo(b, "nome")), "pt-BR")));
                if (response.data.status !== 200 && response.data.status !== 400) setErro(response.data.erro || "Não foi possível carregar os clientes.");
            } catch (err) {
                console.error(err);
                setErro("Não foi possível carregar os clientes. Tente novamente mais tarde.");
            } finally {
                setCarregando(false);
            }
        }

        getUsuarios();
    }, [token])

    const contagem = useMemo(() => ({
        todos: usuarios.length,
        titulares: usuarios.filter((u) => !ehSecretaria(u)).length,
        secretarias: usuarios.filter(ehSecretaria).length,
    }), [usuarios]);

    const usuariosFiltrados = useMemo(() => {
        const porTipo = usuarios.filter((u) => filtro === "todos" || (filtro === "secretarias" ? ehSecretaria(u) : !ehSecretaria(u)));
        return filtrarUsuarios(porTipo, busca);
    }, [usuarios, busca, filtro]);

    const usuariosDaPagina = usuariosFiltrados.slice(pagina * porPagina, pagina * porPagina + porPagina);

    const abrirPerfil = (idUsuario) => idUsuario && navigate(`/EmitirNota/Perfil/${idUsuario}`);

    // Coluna "Vínculo": de quem a secretária é, ou quais secretárias o titular tem
    const vinculo = (usuario) => {
        if (ehSecretaria(usuario)) {
            const responsavel = usuario.responsavel;
            if (!responsavel) return <Typography variant="body2" color="text.disabled">Titular não encontrado</Typography>;
            return (
                <Typography variant="body2" component="div">
                    Secretária de{" "}
                    <Link
                        component="button"
                        underline="hover"
                        sx={{ fontWeight: 500, color: corPrimaria, verticalAlign: "baseline" }}
                        onClick={(e) => { e.stopPropagation(); abrirPerfil(responsavel.idUsuario); }}
                    >
                        {responsavel.nome}
                    </Link>
                </Typography>
            );
        }

        const secretarias = usuario.secretarias || [];
        if (secretarias.length === 0) return <Typography variant="body2" color="text.disabled">—</Typography>;
        return (
            <Tooltip
                title={
                    <Box>
                        {secretarias.map((s) => (
                            <Box key={s.idUsuario} sx={{ py: 0.25 }}>
                                <strong>{s.nome}</strong>{s.email ? ` · ${s.email}` : ""}
                            </Box>
                        ))}
                    </Box>
                }
            >
                <Chip
                    size="small"
                    variant="outlined"
                    label={secretarias.length === 1 ? `1 secretária: ${secretarias[0].nome}` : `${secretarias.length} secretárias`}
                    sx={{ maxWidth: 240 }}
                />
            </Tooltip>
        );
    };


    return (
        <div>
            <Header />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", pb: 6 }}>
                <Box sx={{ width: "90%", maxWidth: 1280, mt: 2.5 }}>
                    <CabecalhoUsuarios
                        titulo="Usuários"
                        descricao={`Clientes cadastrados na plataforma e suas secretárias${carregando ? "" : ` · ${usuarios.length} no total`}.`}
                        acoes={
                            <Tooltip title="Os clientes se cadastram pelo site (em breve)">
                                <span>
                                    <Button variant="contained" size="small" disabled startIcon={<PersonAddAlt1Icon />} sx={{ textTransform: "none" }}>
                                        Adicionar Usuário
                                    </Button>
                                </span>
                            </Tooltip>
                        }
                    />

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
                            <CampoBusca
                                valor={busca}
                                onChange={(valor) => { setBusca(valor); setPagina(0); }}
                                placeholder="Buscar por nome, e-mail, CPF ou telefone"
                            />
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {filtros.map((item) => {
                                    const ativo = filtro === item.chave;
                                    return (
                                        <Chip
                                            key={item.chave}
                                            label={`${item.label} (${contagem[item.chave]})`}
                                            size="small"
                                            variant={ativo ? "filled" : "outlined"}
                                            onClick={() => { setFiltro(item.chave); setPagina(0); }}
                                            sx={ativo ? { backgroundColor: corPrimaria, color: "#fff", "&:hover": { backgroundColor: "#153d66" } } : undefined}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        {erro && <Alert severity="error" sx={{ mx: 2.5, mb: 2 }}>{erro}</Alert>}

                        <TableContainer sx={{ borderTop: 1, borderColor: "divider" }}>
                            <Table size="small" sx={{ minWidth: 860 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#fafafa", "& th": { fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 } }}>
                                        <TableCell>Usuário</TableCell>
                                        <TableCell>CPF</TableCell>
                                        <TableCell>Telefone</TableCell>
                                        <TableCell>Tipo</TableCell>
                                        <TableCell>Vínculo</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {carregando ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 6 }).map((__, j) => <TableCell key={j} sx={{ py: 2 }}><Skeleton /></TableCell>)}
                                            </TableRow>
                                        ))
                                    ) : usuariosDaPagina.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ py: 6, textAlign: "center", borderBottom: 0 }}>
                                                <PersonSearchIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                                                <Typography sx={{ fontWeight: 500, mt: 1 }}>Nenhum usuário encontrado</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {busca || filtro !== "todos" ? "Tente outro termo de busca ou filtro." : "Ainda não há clientes cadastrados."}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        usuariosDaPagina.map((usuario) => {
                                            const nome = campo(usuario, "nome");
                                            const cpf = campo(usuario, "cpf");
                                            const telefone = campo(usuario, "telefone");
                                            const secretaria = ehSecretaria(usuario);
                                            const perfil = idPerfil(usuario);
                                            return (
                                                <TableRow
                                                    key={usuario?.idUsuario}
                                                    hover
                                                    onClick={() => abrirPerfil(perfil)}
                                                    sx={{ cursor: perfil ? "pointer" : "default", "& td": { py: 1.5 } }}
                                                >
                                                    <TableCell>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                            <Avatar
                                                                sx={{
                                                                    width: 36, height: 36, fontSize: 14,
                                                                    backgroundColor: secretaria ? "#e8eef5" : corPrimaria,
                                                                    color: secretaria ? corPrimaria : "#fff",
                                                                }}
                                                            >
                                                                {iniciais(nome)}
                                                            </Avatar>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{nome || "Sem nome"}</Typography>
                                                                    {secretaria && <Chip label="Secretária" size="small" sx={{ height: 18, fontSize: 10 }} />}
                                                                    {usuario?.firstTime && (
                                                                        <Tooltip title="Ainda não concluiu o primeiro acesso à plataforma">
                                                                            <Chip label="Primeiro acesso" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary">{campo(usuario, "email")}</Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{cpf ? MascaraCpf(cpf) : "—"}</TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{telefone ? MascaraTelefone(telefone) : "—"}</TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{tiposPessoa[campo(usuario, "tipoPessoa")] || "—"}</TableCell>
                                                    <TableCell>{vinculo(usuario)}</TableCell>
                                                    <TableCell align="right">
                                                        {perfil && (
                                                            <Button
                                                                size="small"
                                                                endIcon={<ArrowForwardIcon fontSize="small" />}
                                                                sx={{ textTransform: "none", color: corPrimaria, whiteSpace: "nowrap" }}
                                                                onClick={(e) => { e.stopPropagation(); abrirPerfil(perfil); }}
                                                            >
                                                                {secretaria ? "Perfil do titular" : "Abrir perfil"}
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!carregando && usuariosFiltrados.length > 0 && (
                            <TablePagination
                                component="div"
                                count={usuariosFiltrados.length}
                                page={pagina}
                                onPageChange={(e, novaPagina) => setPagina(novaPagina)}
                                rowsPerPage={porPagina}
                                onRowsPerPageChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(0); }}
                                rowsPerPageOptions={[10, 25, 50]}
                                labelRowsPerPage="Por página"
                                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                                sx={{ borderTop: 1, borderColor: "divider" }}
                            />
                        )}
                    </Card>
                </Box>
            </Box>
        </div>
    );
}

export default Usuarios;
